'use client';
import { AskGregSection, ContactSupportSection, HeroBanner, ProductItemHit } from '@workspace/widgets';
import { Typography } from '@workspace/ui/common';
import { usePageHeroBanner } from '@workspace/hooks';
import { InstantSearchProvider } from '@/app/providers';
import type { HeroBannerData } from '@workspace/interfaces';
import { ALGOLIA_CONFIG, convertAttributesNameToKey, handleScrollToElement } from '@/app/lib';
import { Configure, Hits } from 'react-instantsearch';
import {
  AlgoliaClearRefinements,
  AlgoliaMenuSelect,
  AlgoliaPagination,
  AlgoliaSearchBox,
  NoResultsBoundary,
} from '@workspace/widgets';
import { AlgoliaHit } from 'instantsearch.js';
import { ProductHit } from '@workspace/interfaces';
import { useCallback } from 'react';

const getFacetFilters = (categoryId?: string, subcategoryId?: string): string[][] => {
  const facetFilters: string[][] = [];
  if (categoryId) {
    facetFilters.push([`productRange._id:${categoryId}`]);
  }
  if (subcategoryId) {
    facetFilters.push([`productGroup._id:${subcategoryId}`]);
  }
  return facetFilters;
};

export default function ProductsListClient({
  parentSlug,
  slug,
  data,
  heroBannerData,
}: {
  parentSlug: string;
  slug?: string;
  data: {
    ctaText?: string;
    searchIntroText?: string;
    filterSectionLabel?: string;
    searchPlaceholder?: string;
    filters?: {
      attribute: { name: string };
      placeholder?: string;
    }[];
    askGreg?: {
      isEnabled: boolean;
      heading?: string;
      description?: string;
      subheading?: string;
      prompts?: string[];
      isShowButton?: boolean;
      backgroundColor?: string;
    };
    showItemsPerPageSelector?: boolean;
    isIncludedContactSupport?: boolean;
    categoryId?: string;
    subcategoryId?: string;
  };
  heroBannerData?: HeroBannerData;
}) {
  const {
    ctaText,
    searchIntroText,
    filterSectionLabel,
    searchPlaceholder,
    filtersConfig,
    askGreg,
    showItemsPerPageSelector,
    isIncludedContactSupport,
    defaultProductRangeCategoryId,
    defaultProductGroupSubcategoryId,
  } = {
    askGreg: data?.askGreg,
    ctaText: data?.ctaText || 'View detail',
    searchIntroText: data?.searchIntroText || 'You can quickly find your product here',
    filterSectionLabel: data?.filterSectionLabel || 'Filter Products Categories',
    searchPlaceholder: data?.searchPlaceholder || 'Search',
    filtersConfig: data?.filters || [],
    showItemsPerPageSelector:
      typeof data?.showItemsPerPageSelector === 'boolean' ? data.showItemsPerPageSelector : true,
    isIncludedContactSupport:
      typeof data?.isIncludedContactSupport === 'boolean' ? data.isIncludedContactSupport : false,
    defaultProductRangeCategoryId: data?.categoryId,
    defaultProductGroupSubcategoryId: data?.subcategoryId,
  };

  const { heroBannerData: fetchedHeroBanner } = usePageHeroBanner('productsList', slug, !!heroBannerData, parentSlug);
  const pageHeroBanner = heroBannerData ?? fetchedHeroBanner;

  const BoundProductItemHitComponentCallback = useCallback(
    ({ hit }: { hit: AlgoliaHit<ProductHit> }) => <ProductItemHit hit={hit} ctaText={ctaText} />,
    [ctaText],
  );

  return (
    <InstantSearchProvider indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Products}>
      <HeroBanner heroBannerData={pageHeroBanner} disableDefaultFallback />

      <Configure facetFilters={getFacetFilters(defaultProductRangeCategoryId, defaultProductGroupSubcategoryId)} />
      <div id="products-list" className="wrapper lg:py-3xl px-md max-sm:py-lg">
        {/* Search and Filter Section */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6 pt-8 md:pt-0">
          {/* Search Section */}
          <div className="flex-1 md:max-w-122">
            <Typography variant="bodyMd" className="mb-4 text-black font-semibold">
              {searchIntroText}
            </Typography>
            <AlgoliaSearchBox placeholder={searchPlaceholder} />
          </div>

          {/* Filter Section */}
          {filtersConfig && filtersConfig.length > 0 && (
            <div className="flex flex-col w-full md:max-w-104">
              <Typography variant="bodyMd" className="mb-4 text-black font-semibold">
                {filterSectionLabel}
              </Typography>
              <div className="grid grid-cols-2 gap-3 md:flex md:gap-4 justify-between flex-wrap w-full">
                {filtersConfig.map((filter, idx) => (
                  <AlgoliaMenuSelect
                    key={`filter-${idx}-${filter?.attribute?.name}`}
                    menuProps={{
                      attribute: convertAttributesNameToKey(filter?.attribute?.name),
                    }}
                    placeholder={filter.placeholder || 'Select'}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <NoResultsBoundary fallback={null}>
          <AlgoliaClearRefinements className="pt-sm md:pt-md flex justify-center pb-lg md:pb-0" />
        </NoResultsBoundary>
        {/* Result Section */}
        <NoResultsBoundary
          fallback={
            <div className="mx-auto max-w-66 sm:max-w-93.25 flex flex-col flex-1 items-center gap-sm text-center mt-2xl pt-lg md:pt-0 mb-45 md:mt-30 md:mb-80">
              <Typography variant="h6" className="">
                No products found matching your filters
              </Typography>
              <Typography variant="bodyMd">You might want to reset or change your filters</Typography>
              <AlgoliaClearRefinements
                translations={{
                  resetButtonText: 'Clear filters',
                }}
                className="flex justify-center"
              />
            </div>
          }
        >
          <Hits<AlgoliaHit<ProductHit>>
            hitComponent={BoundProductItemHitComponentCallback}
            classNames={{
              list: 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-sm gap-y-xl md:gap-x-5 md:gap-y-xl place-items-center md:place-items-start xl:place-items-start mt-lg md:mt-2xl',
              item: 'w-full',
            }}
          />
          <AlgoliaPagination
            onPageChange={() =>
              handleScrollToElement({
                fromId: 'products-list',
                toClassName: '.hero-banner-container',
              })
            }
            variant="jumper"
            className="mt-8 md:mt-2xl flex-col md:flex-row items-center justify-center mb-lg md:mb-0 gap-2"
            showItemsPerPageSelector={showItemsPerPageSelector}
          />
        </NoResultsBoundary>
      </div>

      {askGreg?.isEnabled && askGreg && (
        <AskGregSection
          title={askGreg?.heading}
          description={askGreg?.description}
          quickPromptsTitle={askGreg?.subheading}
          quickPrompts={askGreg?.prompts?.map((text: string) => ({ text })) || []}
          isShowButton={askGreg?.isShowButton}
          backgroundColor={askGreg?.backgroundColor}
        />
      )}
      <ContactSupportSection
        locator={slug ? { pageType: 'productsList', slug } : undefined}
        isSkip={!isIncludedContactSupport}
        showError
      />
    </InstantSearchProvider>
  );
}
